// ✅ src/pages/VehicleV2/AddTyreSLT.jsx
// Backend mount:
//   app.use("/api/addTyreSLT", addTyreSLT);
//
// Design template aligned with ProductionBOMListing / TyreMaintenanceEntry.
// Logic and APIs unchanged.

import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaArrowLeft,
  FaSave,
  FaSearch,
  FaEdit,
  FaTrash,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/addTyreSLT";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const AddTyreSLT = () => {
  const navigate = useNavigate();
  const query = useQuery();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  const loginUserId =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");
  const branchId = toInt(getLS("branchId") || getLS("branchid"), 8);

  const tyreId = toInt(query.get("tyreId"), 0);
  const initialType = (query.get("type") || "N").toUpperCase();

  const [tyreType, setTyreType] = useState(initialType);
  const [serialNo, setSerialNo] = useState("");
  const [tyreSize, setTyreSize] = useState("");
  const [position, setPosition] = useState("Rear");

  const [serialOptions, setSerialOptions] = useState([]);
  const [sizeOptions, setSizeOptions] = useState([]);

  const [vehicleConnectInventory, setVehicleConnectInventory] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [vehicleBase, setVehicleBase] = useState("DAY");

  const basisParam = useMemo(() => {
    if (vehicleBase === "HOUR") return "Hour";
    if (vehicleBase === "KM") return "KM";
    return "Day";
  }, [vehicleBase]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editRowId, setEditRowId] = useState(null);

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const smallInputClass = `w-full px-2 py-1 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const fetchInit = async () => {
    try {
      const res = await axios.get(`${API_BASE}/init`, {
        params: {
          loginUserId: toInt(loginUserId),
          branchId,
        },
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load init data");
        return;
      }

      setVehicleConnectInventory(!!res.data.vehicleConnectInventory);

      if (res.data.defaultType) {
        setTyreType(String(res.data.defaultType).toUpperCase());
      }

      if (res.data.defaultBasis) {
        const b = String(res.data.defaultBasis).toLowerCase();
        if (b.includes("hour")) setVehicleBase("HOUR");
        else if (b.includes("km")) setVehicleBase("KM");
        else setVehicleBase("DAY");
      }
    } catch (e) {
      toast.error("Server error while loading init");
    }
  };

  const fetchDropdowns = async () => {
    try {
      const res = await axios.get(`${API_BASE}/dropdowns`, {
        params: { branchId, type: tyreType },
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load dropdowns");
        setSerialOptions([]);
        setSizeOptions([]);
        return;
      }

      const s = res.data.serials || [];
      const z = res.data.sizes || [];

      setSerialOptions(
        s.map((x) => ({
          value: x.tyreSerialNo ?? x.value ?? "",
          label: x.tyreSerialNo ?? x.label ?? "",
        }))
      );

      setSizeOptions(
        z.map((x) => ({
          value: x.tyreSize ?? x.value ?? "",
          label: x.tyreSize ?? x.label ?? "",
        }))
      );
    } catch (e) {
      toast.error("Server error while loading dropdowns");
      setSerialOptions([]);
      setSizeOptions([]);
    }
  };

  const fetchGrid = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${API_BASE}/list`, {
        params: {
          branchId,
          type: tyreType,
          tyreSerialNo: serialNo,
          tyreSize,
          position,
          basis: basisParam,
          tyreId,
        },
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load grid");
        setRows([]);
        return;
      }

      const list = res.data.list || [];

      setRows(
        list.map((r) => {
          const local = r._local || {
            vehicleCode: r.vehicleCode ?? "",
            vehicleRegNo: r.vehicleRegNo ?? "",
            fittingDate: r.fittingDate ?? "",
            fittingHourKm: r.fittingHourKm ?? "",
            removingDate: r.removingDate ?? "",
            removingHourKm: r.removingHourKm ?? "",
            runningHourKm: r.runningHourKm ?? "",
            rate: r.rate ?? "",
            tyreCostPerKm: r.tyreCostPerKm ?? "",
          };

          return {
            ...r,
            tyreSerialNoText: Array.isArray(r.tyreSerialNo)
              ? r.tyreSerialNo[0]
              : r.tyreSerialNo ?? "",
            _local: local,
          };
        })
      );
    } catch (e) {
      toast.error("Server error while loading grid");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchDropdowns();
    setSerialNo("");
    setTyreSize("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tyreType, branchId]);

  useEffect(() => {
    fetchGrid();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tyreType, serialNo, tyreSize, position, basisParam, tyreId]);

  const setRowLocal = (rowId, key, value) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? { ...r, _local: { ...(r._local || {}), [key]: value } }
          : r
      )
    );
  };

  const startEdit = (rowId) => setEditRowId(rowId);

  const cancelEdit = () => {
    setEditRowId(null);
    fetchGrid();
  };

  const saveRow = async (row) => {
    try {
      const L = row._local || {};

      const payload = {
        loginUserId: toInt(loginUserId),
        branchId,
        id: toInt(row.id),
        tyreId: toInt(row.tyreId || 0),
        type: tyreType,
        vehicleBase: basisParam,
        vehicleCode: L.vehicleCode,
        vehicleRegNo: L.vehicleRegNo,
        fittingDate: L.fittingDate,
        fittingHourKm: L.fittingHourKm,
        removingDate: L.removingDate,
        removingHourKm: L.removingHourKm,
        runningHourKm: L.runningHourKm,
        rate: L.rate,
        tyreCostPerKm: L.tyreCostPerKm,
      };

      const res = await axios.post(`${API_BASE}/update-row`, payload);

      if (!res.data?.success) {
        toast.error(res.data?.message || "Update failed");
        return;
      }

      toast.success(res.data?.message || "Updated successfully");
      setEditRowId(null);
      fetchGrid();
    } catch (e) {
      toast.error("Server error while updating");
    }
  };

  const doRetread = async (row) => {
    if (!window.confirm("Do you really want to Retread?")) return;
    try {
      const res = await axios.post(`${API_BASE}/retread`, {
        loginUserId: toInt(loginUserId),
        branchId,
        id: toInt(row.id),
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Retread failed");
        return;
      }

      toast.success(res.data?.message || "Retreaded");
      fetchGrid();
    } catch {
      toast.error("Server error while retread");
    }
  };

  const doTransfer = async (row) => {
    if (!window.confirm("Do you really want to Transfer?")) return;
    try {
      const res = await axios.post(`${API_BASE}/transfer`, {
        loginUserId: toInt(loginUserId),
        branchId,
        id: toInt(row.id),
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Transfer failed");
        return;
      }

      toast.success(res.data?.message || "Transferred");
      fetchGrid();
    } catch {
      toast.error("Server error while transfer");
    }
  };

  const doScrap = async (row) => {
    if (!window.confirm("Do you really want to Scrap?")) return;
    try {
      const res = await axios.post(`${API_BASE}/scrap`, {
        loginUserId: toInt(loginUserId),
        branchId,
        id: toInt(row.id),
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Scrap failed");
        return;
      }

      toast.success(res.data?.message || "Scrapped");
      fetchGrid();
    } catch {
      toast.error("Server error while scrap");
    }
  };

  const doDelete = async (row) => {
    if (!window.confirm("Do you really want to Delete?")) return;
    try {
      const res = await axios.post(`${API_BASE}/delete-row`, {
        loginUserId: toInt(loginUserId),
        branchId,
        id: toInt(row.id),
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Delete failed");
        return;
      }

      toast.success(res.data?.message || "Deleted");
      fetchGrid();
    } catch {
      toast.error("Server error while delete");
    }
  };

  const submitAll = async () => {
    try {
      setSaving(true);

      const payload = {
        branchId,
        loginUserId: toInt(loginUserId),
        type: tyreType,
        basis: basisParam,
        rows: rows.map((r) => ({
          id: toInt(r.id),
          tyreId: toInt(r.tyreId || 0),
          useOfTyre: r.useOfTyre || "",
          vehicleCode: r._local?.vehicleCode ?? "",
          vehicleRegNo: r._local?.vehicleRegNo ?? "",
          fittingDate: r._local?.fittingDate ?? "",
          fittingHourKm: r._local?.fittingHourKm ?? "",
          removingDate: r._local?.removingDate ?? "",
          removingHourKm: r._local?.removingHourKm ?? "",
          runningHourKm: r._local?.runningHourKm ?? "",
          rate: r._local?.rate ?? "",
          tyreCostPerKm: r._local?.tyreCostPerKm ?? "",
        })),
      };

      const res = await axios.post(`${API_BASE}/submit`, payload);

      if (!res.data?.success) {
        toast.error(res.data?.message || "Submit failed");
        return;
      }

      toast.success(res.data?.message || "Submitted successfully");
      fetchGrid();
    } catch (e) {
      toast.error("Server error while submit");
    } finally {
      setSaving(false);
    }
  };

  const handleSearch = () => {
    fetchGrid();
  };

  const handleViewAll = () => {
    setSerialNo("");
    setTyreSize("");
    setPosition("Rear");
    setTimeout(() => {
      fetchGrid();
    }, 0);
  };

  const cellClass = `px-4 py-4 text-sm ${
    isDark ? "text-gray-300" : "text-gray-900"
  }`;

  const actionLink = (color, disabled) =>
    `text-xs font-semibold underline transition ${
      disabled
        ? "opacity-40 cursor-not-allowed"
        : color
    }`;

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ADD TYRE</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                <FaArrowLeft />
                Back
              </button>

              <h2
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Add Tyre
              </h2>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={submitAll}
                disabled={saving || loading}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${
                  saving || loading ? "opacity-60 cursor-not-allowed" : ""
                } ${buttonClass} ${hoverClass} text-white`}
              >
                <FaSave />
                {saving ? "Saving..." : "Submit"}
              </button>

              <button
                onClick={() => setShowFilters((prev) => !prev)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div
              className={`p-6 rounded-lg mb-6 ${
                isDark ? "bg-gray-800/50" : "bg-blue-50"
              }`}
            >
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3
                  className={`text-lg font-semibold ${
                    isDark ? "text-white" : "text-blue-900"
                  }`}
                >
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                  <button
                    onClick={handleViewAll}
                    disabled={loading}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={handleSearch}
                    disabled={loading}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-6 mb-6 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={tyreType === "N"}
                    onChange={() => setTyreType("N")}
                    className="w-4 h-4 cursor-pointer"
                  />
                  <span
                    className={`text-sm font-medium ${
                      isDark ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    New Tyre
                  </span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={tyreType === "R"}
                    onChange={() => setTyreType("R")}
                    className="w-4 h-4 cursor-pointer"
                  />
                  <span
                    className={`text-sm font-medium ${
                      isDark ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    Retreaded Tyre
                  </span>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className={labelClass}>Tyre Serial No</label>
                  <select
                    value={serialNo}
                    onChange={(e) => setSerialNo(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select</option>
                    {serialOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Tyre Size / Ply Rating</label>
                  <select
                    value={tyreSize}
                    onChange={(e) => setTyreSize(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select</option>
                    {sizeOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Position</label>
                  <select
                    value={position}
                    onChange={(e) => setPosition(e.target.value)}
                    className={inputClass}
                  >
                    <option value="Front">Front</option>
                    <option value="Rear">Rear</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={vehicleBase === "HOUR"}
                    onChange={() => setVehicleBase("HOUR")}
                    className="w-4 h-4 cursor-pointer"
                  />
                  <span className="text-sm font-medium">Hour Base Vehicle</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={vehicleBase === "KM"}
                    onChange={() => setVehicleBase("KM")}
                    className="w-4 h-4 cursor-pointer"
                  />
                  <span className="text-sm font-medium">Km Base Vehicle</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={vehicleBase === "DAY"}
                    onChange={() => setVehicleBase("DAY")}
                    className="w-4 h-4 cursor-pointer"
                  />
                  <span className="text-sm font-medium">Day Base Vehicle</span>
                </label>
              </div>

              {vehicleConnectInventory && (
                <p
                  className={`mt-4 text-sm ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Item Name enabled (Inventory connect).
                </p>
              )}
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {loading ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Loading...
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  No Records found
                </div>
              </div>
            ) : (
              <>
                <table
                  className={`w-full min-w-[1800px] ${
                    isDark ? "bg-gray-800" : "bg-white"
                  }`}
                >
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      {[
                        "S.No",
                        "Tyre Serial No",
                        "Tyre Size",
                        "Tyre Position",
                        "Vehicle Code",
                        "Vehicle Reg No",
                        "Fitting Date",
                        "Fitting Hour/KM",
                        "Removing Date",
                        "Removing Hour/KM",
                        "Running Hour/KM",
                        "Rate",
                        "Tyre Cost Per Km",
                        "Retread / Original",
                      ].map((h) => (
                        <th
                          key={h}
                          className="px-4 py-4 text-left text-sm font-semibold"
                        >
                          {h}
                        </th>
                      ))}
                      <th className="px-4 py-4 text-center text-sm font-semibold">
                        Actions
                      </th>
                      <th className="px-4 py-4 text-left text-sm font-semibold">
                        Branch
                      </th>
                    </tr>
                  </thead>

                  <tbody
                    className={`${
                      isDark ? "divide-gray-700" : "divide-gray-200"
                    } divide-y`}
                  >
                    {rows.map((r, idx) => {
                      const editing = editRowId === r.id;

                      return (
                        <tr
                          key={r.id || idx}
                          className={`${
                            idx % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-white"
                              : isDark
                              ? "bg-gray-750"
                              : "bg-gray-50"
                          } transition-colors ${
                            isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                          }`}
                        >
                          <td className={cellClass}>{r.slno ?? idx + 1}</td>
                          <td
                            className={`px-4 py-4 text-sm font-semibold ${
                              isDark ? "text-green-400" : "text-green-600"
                            }`}
                          >
                            {r.tyreSerialNoText || "-"}
                          </td>
                          <td className={cellClass}>{r.tyreSize || "-"}</td>
                          <td className={cellClass}>
                            {r.tyrePosition || "-"}
                          </td>

                          <td className="px-2 py-2 min-w-[130px]">
                            <input
                              disabled={!editing}
                              value={r._local?.vehicleCode ?? ""}
                              onChange={(e) =>
                                setRowLocal(r.id, "vehicleCode", e.target.value)
                              }
                              className={smallInputClass}
                            />
                          </td>
                          <td className="px-2 py-2 min-w-[130px]">
                            <input
                              disabled={!editing}
                              value={r._local?.vehicleRegNo ?? ""}
                              onChange={(e) =>
                                setRowLocal(
                                  r.id,
                                  "vehicleRegNo",
                                  e.target.value
                                )
                              }
                              className={smallInputClass}
                            />
                          </td>
                          <td className="px-2 py-2 min-w-[120px]">
                            <input
                              disabled={!editing}
                              value={r._local?.fittingDate ?? ""}
                              onChange={(e) =>
                                setRowLocal(r.id, "fittingDate", e.target.value)
                              }
                              className={smallInputClass}
                              placeholder="dd/MM/yyyy"
                            />
                          </td>
                          <td className="px-2 py-2 min-w-[110px]">
                            <input
                              disabled={!editing}
                              value={r._local?.fittingHourKm ?? ""}
                              onChange={(e) =>
                                setRowLocal(
                                  r.id,
                                  "fittingHourKm",
                                  e.target.value
                                )
                              }
                              className={smallInputClass}
                            />
                          </td>
                          <td className="px-2 py-2 min-w-[120px]">
                            <input
                              disabled={!editing}
                              value={r._local?.removingDate ?? ""}
                              onChange={(e) =>
                                setRowLocal(
                                  r.id,
                                  "removingDate",
                                  e.target.value
                                )
                              }
                              className={smallInputClass}
                              placeholder="dd/MM/yyyy"
                            />
                          </td>
                          <td className="px-2 py-2 min-w-[110px]">
                            <input
                              disabled={!editing}
                              value={r._local?.removingHourKm ?? ""}
                              onChange={(e) =>
                                setRowLocal(
                                  r.id,
                                  "removingHourKm",
                                  e.target.value
                                )
                              }
                              className={smallInputClass}
                            />
                          </td>
                          <td className="px-2 py-2 min-w-[110px]">
                            <input
                              disabled={!editing}
                              value={r._local?.runningHourKm ?? ""}
                              onChange={(e) =>
                                setRowLocal(
                                  r.id,
                                  "runningHourKm",
                                  e.target.value
                                )
                              }
                              className={smallInputClass}
                            />
                          </td>
                          <td className="px-2 py-2 min-w-[90px]">
                            <input
                              disabled={!editing}
                              value={r._local?.rate ?? ""}
                              onChange={(e) =>
                                setRowLocal(r.id, "rate", e.target.value)
                              }
                              className={smallInputClass}
                            />
                          </td>
                          <td className="px-2 py-2 min-w-[110px]">
                            <input
                              disabled={!editing}
                              value={r._local?.tyreCostPerKm ?? ""}
                              onChange={(e) =>
                                setRowLocal(
                                  r.id,
                                  "tyreCostPerKm",
                                  e.target.value
                                )
                              }
                              className={smallInputClass}
                            />
                          </td>

                          <td className={cellClass}>
                            {r.status || r.useOfTyre || "-"}
                          </td>

                          <td className="px-4 py-4 text-center whitespace-nowrap">
                            {editing ? (
                              <div className="flex items-center justify-center gap-3">
                                <button
                                  type="button"
                                  onClick={() => saveRow(r)}
                                  className="text-green-600 hover:text-green-700 text-xs font-semibold underline"
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelEdit}
                                  className="text-gray-500 hover:text-gray-700 text-xs font-semibold underline"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center gap-3 flex-wrap">
                                <button
                                  type="button"
                                  onClick={() => startEdit(r.id)}
                                  className="text-yellow-500 hover:text-yellow-600 text-lg transition"
                                  title="Edit"
                                >
                                  <FaEdit />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => doRetread(r)}
                                  disabled={!!editing}
                                  className={actionLink(
                                    isDark
                                      ? "text-blue-400 hover:text-blue-300"
                                      : "text-blue-600 hover:text-blue-800",
                                    false
                                  )}
                                >
                                  Retread
                                </button>
                                <button
                                  type="button"
                                  onClick={() => doTransfer(r)}
                                  className={actionLink(
                                    isDark
                                      ? "text-indigo-300 hover:text-indigo-200"
                                      : "text-indigo-600 hover:text-indigo-800",
                                    false
                                  )}
                                >
                                  Transfer
                                </button>
                                <button
                                  type="button"
                                  onClick={() => doScrap(r)}
                                  className={actionLink(
                                    isDark
                                      ? "text-yellow-300 hover:text-yellow-200"
                                      : "text-yellow-700 hover:text-yellow-800",
                                    false
                                  )}
                                >
                                  Scrap
                                </button>
                                <button
                                  type="button"
                                  onClick={() => doDelete(r)}
                                  className="text-red-500 hover:text-red-600 text-lg transition"
                                  title="Delete"
                                >
                                  <FaTrash />
                                </button>
                              </div>
                            )}
                          </td>

                          <td className={cellClass}>{r.branchName || "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="p-4 flex justify-end">
                  <button
                    onClick={submitAll}
                    disabled={saving || loading}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold shadow transition ${
                      saving || loading ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSave />
                    {saving ? "Saving..." : "Submit"}
                  </button>
                </div>
              </>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default AddTyreSLT;